import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Car, ArrowRight } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const invite = searchParams.get("invite");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("La password deve avere almeno 6 caratteri");
      return;
    }
    if (!acceptedTerms) {
      toast.error("Devi accettare i Termini e Condizioni per registrarti");
      return;
    }
    setLoading(true);
    const res = await register(email, password, name);
    setLoading(false);
    if (res.ok) {
      toast.success("Account creato!");
      nav(invite ? `/onboarding?invite=${encodeURIComponent(invite)}` : "/onboarding");
    } else {
      toast.error(res.error || "Registrazione fallita");
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#F5F6FA]">
      <div className="hidden lg:flex relative bg-gradient-to-br from-[#1A2A6C] via-[#2D3F8F] to-[#3D4FA0] p-12 items-center justify-center overflow-hidden text-white">
        <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-white/10 blur-2xl" />
        <div className="absolute -bottom-32 -left-12 w-[26rem] h-[26rem] rounded-full bg-white/5 blur-2xl" />
        <div className="relative z-10 max-w-md anim-slide-up">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur border border-white/20 text-xs font-semibold mb-6">
            <Car className="w-3.5 h-3.5" /> Crea il tuo account
          </div>
          <h1 className="font-heading text-5xl xl:text-6xl font-bold tracking-tight leading-[1.05] mb-6">
            Una <span className="text-[#FF9E1B]">community</span> di genitori che si aiutano.
          </h1>
          <p className="text-lg font-medium text-white/85 leading-relaxed">
            Registrati e ricevi (o crea) il codice invito del tuo gruppo: classe, squadra, condominio.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md anim-slide-up">
          <Link to="/" className="inline-flex items-center gap-2.5 mb-8" data-testid="register-logo">
            <img src="/hopzigo-logo.png" alt="Hopzigo" className="h-12 w-auto" />
          </Link>
          <h2 className="font-heading text-3xl font-bold tracking-tight mb-2">Crea il tuo profilo</h2>
          <p className="text-[#8A91A8] mb-8">In meno di un minuto sei nel gruppo.</p>

          <form onSubmit={onSubmit} className="space-y-5">
            <div>
              <label className="brut-label" htmlFor="name">Nome e cognome</label>
              <input id="name" type="text" className="brut-input" placeholder="Mario Rossi" value={name} onChange={(e) => setName(e.target.value)} required data-testid="register-name-input" />
            </div>
            <div>
              <label className="brut-label" htmlFor="email">Email</label>
              <input id="email" type="email" className="brut-input" placeholder="mario@famiglia.it" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="register-email-input" />
            </div>
            <div>
              <label className="brut-label" htmlFor="password">Password</label>
              <input id="password" type="password" className="brut-input" placeholder="Almeno 6 caratteri" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="register-password-input" />
            </div>
            <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="register-terms-label">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="mt-1 w-5 h-5 rounded border-2 border-[#ECEEF5] text-[#1A2A6C] focus:ring-2 focus:ring-[#1A2A6C]/30 shrink-0 cursor-pointer accent-[#1A2A6C]"
                required
                data-testid="register-terms-checkbox"
              />
              <span className="text-sm text-[#1A1D29] leading-snug">
                Accetto i{" "}
                <a
                  href="/hopzigo-termini-condizioni.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-[#1A2A6C] underline hover:text-[#FF9E1B]"
                  data-testid="register-terms-link"
                >
                  Termini e Condizioni
                </a>{" "}
                di Hopzigo.
              </span>
            </label>
            <button type="submit" className="brut-btn w-full text-base disabled:opacity-60 disabled:cursor-not-allowed" disabled={loading || !acceptedTerms} data-testid="register-submit-button">
              {loading ? "Creazione…" : (<>Crea account <ArrowRight className="w-4 h-4" /></>)}
            </button>
          </form>

          <p className="mt-8 text-sm text-[#8A91A8]">
            Hai già un account?{" "}
            <Link to="/login" className="font-semibold text-[#1A2A6C] hover:underline" data-testid="register-login-link">
              Accedi
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
